/**
 * The signed-in person's own preferences (T01-17): saved views + columns of every table, tours
 * seen, "Get your shop ready" hidden … — one JSON object per staff member at identity-service
 * (`GET/PATCH /v1/auth/me {preferences}`), so the same person sees them on every computer.
 *
 * - Reads are synchronous (screens render from it at once): the last copy from the server, or the
 *   copy this browser keeps (`localStorage`, also used while offline / before the first answer).
 * - Writes change the value right away, keep it in this browser and are sent to identity a moment
 *   later (500 ms, several keys in one `PATCH`; a key replaces its value as a whole, `null`
 *   removes it — two tabs writing different keys never lose each other's change).
 * - {@link PreferencesSync} loads them after sign-in and again when identity says they changed on
 *   another computer (hub topic `staff-self:<staffId>`, R21).
 * - Choices made before this existed (the per-browser keys of T11-05/47, T09-30) move to the
 *   account once, the first time the person signs in with an empty account copy; once the
 *   account copy is in step, those old keys are removed from this browser.
 * - Sign-out ({@link forgetPreferencesHere}): waiting writes are sent, then this person's copy is
 *   removed from this browser — on a shared shop computer the next person must not read someone
 *   else's saved searches (they may hold customer names). The account copy stays at identity.
 */
import { useSyncExternalStore } from "react";

export type PreferenceValues = Record<string, unknown>;

export interface MePreferences {
  preferences: PreferenceValues;
  preferencesVersion: number;
}

/** Talks to identity for one staff member (set by `PreferencesSync`). */
export interface PreferencesRemote {
  patch(body: {
    preferences?: Record<string, unknown>;
    locale?: "ja" | "th" | "en";
  }): Promise<MePreferences>;
}

interface Entry {
  values: PreferenceValues;
  version: number;
  /** An answer of identity arrived at least once (this session). */
  synced: boolean;
}

const MIRROR_PREFIX = "tcg.admin.prefs.";
/** A language picked while nobody was signed in (the sign-in page) — saved to the next account. */
export const LOCALE_CHOICE_KEY = "tcg.admin.locale-choice";
/** Preference key: the person picked a UI language themself (their language follows them). */
export const LOCALE_CHOSEN = "ui:locale-chosen";
export const FLUSH_DELAY_MS = 500;
const SIGN_OUT_FLUSH_MS = 3_000;
const RETRY_MS = [5_000, 15_000, 60_000] as const;

const entries = new Map<string, Entry>();
const pending = new Map<string, Map<string, unknown>>();
const remotes = new Map<string, PreferencesRemote>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const inflight = new Map<string, Promise<void>>();
const failures = new Map<string, number>();
const listeners = new Set<() => void>();
let activeStaff: string | null = null;

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function without(values: PreferenceValues, key: string): PreferenceValues {
  return Object.fromEntries(Object.entries(values).filter(([name]) => name !== key));
}

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function loadMirror(staffId: string): Entry {
  try {
    const raw = storage()?.getItem(MIRROR_PREFIX + staffId);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (isRecord(parsed) && isRecord(parsed.values)) {
        const version = typeof parsed.version === "number" ? parsed.version : 0;
        return { values: parsed.values, version, synced: false };
      }
    }
  } catch {
    // unreadable copy: start empty (the server copy arrives after sign-in)
  }
  return { values: legacyPreferences(staffId), version: 0, synced: false };
}

function parsed(raw: string | null): unknown {
  if (raw === null) {
    return undefined;
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * Choices this browser kept per person before preferences moved to the account (T11-05 tables,
 * T11-47 home checklist/tour, T09-30 theme editor tour) → preference keys. Read once, when this
 * browser has no copy yet; they reach the account at the first sign-in if the account is empty.
 */
export function legacyPreferences(staffId: string): PreferenceValues {
  const store = storage();
  const out: PreferenceValues = {};
  if (!store) {
    return out;
  }
  const views = `tcg.admin.views:${staffId}:`;
  const table = `tcg.admin.table:${staffId}:`;
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (!key) {
      continue;
    }
    if (key.startsWith(views)) {
      const value = parsed(store.getItem(key));
      if (Array.isArray(value) && value.length > 0) {
        out[`views:${key.slice(views.length)}`] = value;
      }
    } else if (key.startsWith(table)) {
      const value = parsed(store.getItem(key));
      if (isRecord(value)) {
        out[`table:${key.slice(table.length)}`] = value;
      }
    }
  }
  const onboarding = parsed(store.getItem(`tcg.admin.onboarding.${staffId}`));
  if (isRecord(onboarding)) {
    out.onboarding = onboarding;
  }
  if (store.getItem(`tcg.admin.tour.theme-editor.v1:${staffId}`) === "done") {
    out["tour:theme-editor"] = true;
  }
  return out;
}

/** The per-browser keys {@link legacyPreferences} reads, for one person. */
function legacyKeys(staffId: string): string[] {
  const store = storage();
  if (!store) {
    return [];
  }
  const views = `tcg.admin.views:${staffId}:`;
  const table = `tcg.admin.table:${staffId}:`;
  const keys: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && (key.startsWith(views) || key.startsWith(table))) {
      keys.push(key);
    }
  }
  for (const key of [
    `tcg.admin.onboarding.${staffId}`,
    `tcg.admin.tour.theme-editor.v1:${staffId}`,
  ]) {
    if (store.getItem(key) !== null) {
      keys.push(key);
    }
  }
  return keys;
}

function removeKeys(keys: readonly string[]): void {
  const store = storage();
  for (const key of keys) {
    try {
      store?.removeItem(key);
    } catch {
      // storage refused: nothing more to do
    }
  }
}

function entry(staffId: string): Entry {
  let found = entries.get(staffId);
  if (!found) {
    found = loadMirror(staffId);
    entries.set(staffId, found);
  }
  return found;
}

function saveMirror(staffId: string, value: Entry): void {
  try {
    storage()?.setItem(
      MIRROR_PREFIX + staffId,
      JSON.stringify({ values: value.values, version: value.version }),
    );
  } catch {
    // private mode / quota: the value lasts for this tab (and the account copy)
  }
}

/** The stored value of `key` for this person (`undefined` = never set). */
export function readPreference(staffId: string, key: string): unknown {
  return entry(staffId).values[key];
}

export function preferencesVersion(staffId: string): number {
  return entry(staffId).version;
}

export function preferencesSynced(staffId: string): boolean {
  return entry(staffId).synced;
}

/** Set (or with `null` remove) `key`: at once here, in this browser, and at identity shortly. */
export function writePreference(staffId: string, key: string, value: unknown): void {
  const current = entry(staffId);
  let values: PreferenceValues;
  if (value === null || value === undefined) {
    if (!(key in current.values)) {
      return;
    }
    values = without(current.values, key);
  } else {
    values = { ...current.values, [key]: value };
  }
  const next = { ...current, values };
  entries.set(staffId, next);
  saveMirror(staffId, next);
  let queue = pending.get(staffId);
  if (!queue) {
    queue = new Map();
    pending.set(staffId, queue);
  }
  queue.set(key, value ?? null);
  emit();
  schedule(staffId, FLUSH_DELAY_MS);
}

function schedule(staffId: string, delay: number): void {
  if (!remotes.has(staffId)) {
    return; // not signed in here yet: sent when PreferencesSync connects
  }
  const timer = timers.get(staffId);
  if (timer !== undefined) {
    clearTimeout(timer);
  }
  timers.set(
    staffId,
    setTimeout(() => {
      timers.delete(staffId);
      void flushPreferences(staffId);
    }, delay),
  );
}

/** Send what is waiting now (tests; sign-out). Resolves when identity answered. */
export async function flushPreferences(staffId: string): Promise<void> {
  const running = inflight.get(staffId);
  if (running) {
    await running;
  }
  const remote = remotes.get(staffId);
  const queue = pending.get(staffId);
  if (!remote || !queue || queue.size === 0) {
    return;
  }
  const sent = Object.fromEntries(queue);
  pending.delete(staffId);
  const run = (async () => {
    try {
      const answer = await remote.patch({ preferences: sent });
      failures.delete(staffId);
      hydratePreferences(staffId, answer);
    } catch (error) {
      const status = (error as { status?: unknown }).status;
      if (typeof status === "number" && status >= 400 && status < 500 && status !== 409) {
        // refused for good (too large, bad key): the value stays in this browser only
        return;
      }
      // offline / server trouble: put the keys back (newer writes win) and try again later
      const queueNow = pending.get(staffId) ?? new Map<string, unknown>();
      for (const [key, value] of Object.entries(sent)) {
        if (!queueNow.has(key)) {
          queueNow.set(key, value);
        }
      }
      pending.set(staffId, queueNow);
      const count = failures.get(staffId) ?? 0;
      failures.set(staffId, count + 1);
      if (count < RETRY_MS.length) {
        schedule(staffId, RETRY_MS[count] ?? 60_000);
      }
    }
  })();
  inflight.set(staffId, run);
  try {
    await run;
  } finally {
    inflight.delete(staffId);
  }
}

/**
 * Identity's copy arrived (`GET /v1/auth/me` or a `PATCH` answer): it becomes the stored copy,
 * except keys written here that were not sent yet (they are newer). An older answer than the copy
 * we already have is ignored.
 */
export function hydratePreferences(staffId: string, me: MePreferences): void {
  const current = entry(staffId);
  if (current.synced && me.preferencesVersion < current.version) {
    return;
  }
  if (!current.synced && me.preferencesVersion === 0 && Object.keys(me.preferences).length === 0) {
    // the account has never been saved: what this browser kept goes to it (once)
    const queue = pending.get(staffId) ?? new Map<string, unknown>();
    for (const [key, value] of Object.entries(current.values)) {
      if (!queue.has(key)) {
        queue.set(key, value);
      }
    }
    if (queue.size > 0) {
      pending.set(staffId, queue);
      schedule(staffId, FLUSH_DELAY_MS);
    }
  }
  let values: PreferenceValues = { ...me.preferences };
  for (const [key, value] of pending.get(staffId) ?? []) {
    values = value === null ? without(values, key) : { ...values, [key]: value };
  }
  const next: Entry = { values, version: me.preferencesVersion, synced: true };
  entries.set(staffId, next);
  saveMirror(staffId, next);
  if ((pending.get(staffId)?.size ?? 0) === 0) {
    // the account holds everything now (the one-time move was answered, or it had its own copy)
    removeKeys(legacyKeys(staffId));
  }
  emit();
}

/**
 * Sign-out (T01-17 review): send what is still waiting (while the sign-in still works), then
 * forget this person's preferences on this computer — memory and `localStorage`, the old
 * per-browser keys too. Their account copy at identity is untouched (next sign-in loads it).
 */
export async function forgetPreferencesHere(staffId: string): Promise<void> {
  const timer = timers.get(staffId);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(staffId);
  }
  try {
    // never hold the sign-out up for long (offline): what could not be sent is dropped
    await Promise.race([
      flushPreferences(staffId),
      new Promise<void>((resolve) => setTimeout(resolve, SIGN_OUT_FLUSH_MS)),
    ]);
  } catch {
    // could not send: the change stays unsaved rather than on this shared computer
  }
  setPreferencesRemote(staffId, null);
  entries.delete(staffId);
  pending.delete(staffId);
  failures.delete(staffId);
  removeKeys([MIRROR_PREFIX + staffId, ...legacyKeys(staffId)]);
  emit();
}

/** Connect (or with `null` disconnect) this person's account; waiting writes are sent. */
export function setPreferencesRemote(staffId: string, remote: PreferencesRemote | null): void {
  if (remote) {
    remotes.set(staffId, remote);
    activeStaff = staffId;
    if ((pending.get(staffId)?.size ?? 0) > 0) {
      schedule(staffId, FLUSH_DELAY_MS);
    }
  } else {
    remotes.delete(staffId);
    const timer = timers.get(staffId);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.delete(staffId);
    }
    if (activeStaff === staffId) {
      activeStaff = null;
    }
  }
}

/**
 * The language switch (T11-03): signed in → saved to the person's account (the language follows
 * them); on the sign-in page → remembered here and saved to the account that signs in next.
 */
export function rememberLocaleChoice(locale: "ja" | "th" | "en"): void {
  const remote = activeStaff ? remotes.get(activeStaff) : undefined;
  const staffId = activeStaff;
  if (!remote || !staffId) {
    keepLocaleChoice(locale);
    return;
  }
  void remote
    .patch({ locale, preferences: { [LOCALE_CHOSEN]: true } })
    .then((answer) => {
      hydratePreferences(staffId, answer);
    })
    .catch(() => {
      keepLocaleChoice(locale); // sent again at the next sign-in
    });
}

/** How long a language picked on the sign-in page waits for a sign-in (another person later on
 * the same computer does not inherit it). */
export const LOCALE_CHOICE_TTL_MS = 30 * 60_000;

function keepLocaleChoice(locale: "ja" | "th" | "en"): void {
  try {
    storage()?.setItem(LOCALE_CHOICE_KEY, JSON.stringify({ locale, at: Date.now() }));
  } catch {
    // the choice lasts for this tab
  }
}

/** A language picked before sign-in (then forgotten: it goes to the account once). */
export function takeLocaleChoice(): "ja" | "th" | "en" | null {
  try {
    const raw = storage()?.getItem(LOCALE_CHOICE_KEY) ?? null;
    storage()?.removeItem(LOCALE_CHOICE_KEY);
    const value = parsed(raw);
    if (!isRecord(value) || typeof value.at !== "number") {
      return null;
    }
    if (Date.now() - value.at > LOCALE_CHOICE_TTL_MS) {
      return null;
    }
    const locale = value.locale;
    return locale === "ja" || locale === "th" || locale === "en" ? locale : null;
  } catch {
    return null;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith(MIRROR_PREFIX)) {
      const staffId = event.key.slice(MIRROR_PREFIX.length);
      // another tab of this browser saved: take its copy (the server copy is the same)
      const mirrored = loadMirror(staffId);
      const current = entries.get(staffId);
      if (!current || mirrored.version >= current.version) {
        entries.set(staffId, { ...mirrored, synced: current?.synced ?? false });
        listener();
      }
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

/** React: the value of `key` for this person, re-rendered whenever it changes (any computer). */
export function usePreference(staffId: string, key: string): unknown {
  return useSyncExternalStore(
    subscribe,
    () => readPreference(staffId, key),
    () => undefined,
  );
}

/** Tests: forget everything in memory (the browser copy is cleared with `localStorage.clear()`). */
export function resetPreferencesForTests(): void {
  for (const timer of timers.values()) {
    clearTimeout(timer);
  }
  entries.clear();
  pending.clear();
  remotes.clear();
  timers.clear();
  inflight.clear();
  failures.clear();
  activeStaff = null;
}
